import { getRpcCurrentFleetId } from "./generated/calls/rpc-current-fleet-id/rpc-current-fleet-id";
import { getRpcRucStatusForFleet } from "./generated/calls/rpc-ruc-status-for-fleet/rpc-ruc-status-for-fleet";
import { getRpcRucStatusForVehicle } from "./generated/calls/rpc-ruc-status-for-vehicle/rpc-ruc-status-for-vehicle";
import {
  deleteRucLicenses,
  getRucLicenses,
  patchRucLicenses,
  postRucLicenses,
} from "./generated/calls/ruc-licenses/ruc-licenses";
import type {
  GetRpcRucStatusForVehicle200Item as RucStatusDTO,
  RucLicenses as RucLicenseDTO,
  RucLicensesInsert as RucLicenseInsertDTO,
  RucLicensesUpdate as RucLicenseUpdateDTO,
} from "./generated/models";
import { buildHeaders, headersToObject } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResult, APIResultNoContent } from "./types";

export type { RucLicenseDTO, RucLicenseInsertDTO, RucLicenseUpdateDTO, RucStatusDTO };

/**
 * Fetches every RUC license row for the current fleet.
 *
 * No `fleet_id` filter is sent - row-level security already scopes reads to
 * the caller's own fleet, matching fetchFleetVehicles/fetchFleetMaintenanceRecords.
 */
async function fetchFleetRucLicenses(): Promise<APIResult<RucLicenseDTO[]>> {
  try {
    const headers = buildHeaders();
    const res = await getRucLicenses(undefined, { headers });

    switch (res.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: res.data,
        };
      default:
        throw postgrestError("Fetch failed", res);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches the RUC license history for a single vehicle.
 *
 * `vehicleId` is the vehicle's external id (`vehicles.id`, matching
 * `Vehicle.externalId`), not a local/domain id.
 */
async function fetchRucLicensesForVehicle(
  vehicleId: string,
): Promise<APIResult<RucLicenseDTO[]>> {
  try {
    const headers = buildHeaders();
    const res = await getRucLicenses(
      { vehicle_id: postgrestFilter(vehicleId) },
      { headers },
    );

    switch (res.status) {
      case 200:
      case 206:
        return {
          success: true,
          data: res.data,
        };
      default:
        throw postgrestError("Fetch failed", res);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Creates a new RUC license row.
 */
async function createRucLicense(
  data: RucLicenseInsertDTO,
): Promise<APIResult<RucLicenseDTO>> {
  try {
    const headers = buildHeaders();
    const res = await postRucLicenses(data, undefined, {
      headers: headersToObject(headers),
    });

    switch (res.status) {
      case 201: {
        // An insert returns the rows it created, so a single insert returns
        // a one-element array.
        const created = res.data[0];

        if (!created) {
          throw new Error("Create succeeded but returned no RUC license");
        }

        return {
          success: true,
          data: created,
        };
      }
      default:
        throw postgrestError("Create failed", res);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Updates a single RUC license row.
 */
async function updateRucLicense(
  licenseId: string,
  data: RucLicenseUpdateDTO,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const res = await patchRucLicenses(
      data,
      { id: postgrestFilter(licenseId) },
      { headers: headersToObject(headers) },
    );

    switch (res.status) {
      case 200:
        return { success: true };
      default:
        throw postgrestError("Update failed", res);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Deletes a single RUC license row.
 */
async function deleteRucLicense(licenseId: string): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const res = await deleteRucLicenses(
      { id: postgrestFilter(licenseId) },
      { headers },
    );

    switch (res.status) {
      case 200:
        return { success: true };
      default:
        throw postgrestError("Delete failed", res);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches the RUC status for a single vehicle.
 *
 * `ruc_status_for_vehicle()` is `RETURNS SETOF`, but a single vehicle only
 * ever has 0-1 rows, so the array is unwrapped here.
 *
 * @returns The vehicle's RUC status, or `null` when it has no RUC license.
 */
async function getRucStatusForVehicle(
  vehicleId: string,
): Promise<APIResult<RucStatusDTO | null>> {
  try {
    const headers = buildHeaders();
    const res = await getRpcRucStatusForVehicle(
      { p_vehicle_id: vehicleId },
      { headers },
    );

    if (res.status !== 200) {
      throw postgrestError("Fetch failed", res);
    }

    return {
      success: true,
      data: res.data[0] ?? null,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

/**
 * Fetches the RUC status for every vehicle in the current fleet.
 *
 * `ruc_status_for_fleet()` takes a fleet id rather than inferring it from the
 * caller like a table read does. Pass `fleetId` when the caller already has
 * one (e.g. `fleetStore.fleetId`) to skip resolving it; omit it to have it
 * resolved from the authenticated user's context.
 */
async function getRucStatusForFleet(
  fleetId?: string,
): Promise<APIResult<RucStatusDTO[]>> {
  try {
    const headers = buildHeaders();

    let resolvedFleetId = fleetId;

    if (!resolvedFleetId) {
      const fleetIdRes = await getRpcCurrentFleetId({ headers });

      if (fleetIdRes.status !== 200) {
        throw postgrestError("Resolving the current fleet failed", fleetIdRes);
      }

      resolvedFleetId = fleetIdRes.data;
    }

    const statusRes = await getRpcRucStatusForFleet(
      { p_fleet_id: resolvedFleetId },
      { headers },
    );

    if (statusRes.status !== 200) {
      throw postgrestError("Fetch failed", statusRes);
    }

    return {
      success: true,
      data: statusRes.data,
    };
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

export {
  createRucLicense,
  deleteRucLicense,
  fetchFleetRucLicenses,
  fetchRucLicensesForVehicle,
  getRucStatusForFleet,
  getRucStatusForVehicle,
  updateRucLicense,
};
